<template>
  <b-row no-gutters>
    <b-col>
      <!-- OKV-234 restyle: self-contained "Caratteristiche test" card (no longer the shared
           CustomCollapse) so header chips, column layout and the orange driver bars match the design. -->
      <div class="et-features-card">
        <div class="et-features-head" :class="{ 'is-open': isOpen }">
          <span class="et-features-title">{{ $t('efficiencyTestDetails.profileFeature') }}</span>

          <div class="et-chips">
            <span class="et-chip">
              <span class="et-chip-label">{{ $t('efficiencyTestDetails.features.chipCriteria') }}</span>
              <span class="et-chip-value">{{ getSummary.criteriaUsedCount || 0 }}</span>
            </span>
            <span class="et-chip">
              <span class="et-chip-label">{{ $t('efficiencyTestDetails.summary.comparablesCount') }}</span>
              <span class="et-chip-value">{{ getSummary.comparablesCount || 0 }}</span>
            </span>
            <span class="et-chip">
              <span class="et-chip-label">{{ $t('efficiencyTestDetails.features.chipScoring') }}</span>
              <span class="et-chip-value">{{ getSummary.scoringProfileModelName }}</span>
            </span>
            <span class="et-chip">
              <span class="et-chip-label">{{ $t('efficiencyTestDetails.features.chipThreshold') }}</span>
              <span class="et-chip-value">{{ $n((getSummary.passThresholdPercent || 0) / 100, 'percent') }}</span>
            </span>
          </div>

          <button type="button" class="et-toggle" @click="toggleOpen">
            {{
              isOpen
                ? $t('efficiencyTestDetails.features.hideDetails')
                : $t('efficiencyTestDetails.features.showDetails')
            }}
            <font-awesome-icon class="et-toggle-icon" :icon="isOpen ? 'chevron-up' : 'chevron-down'" />
          </button>
        </div>

        <div v-if="isOpen" class="et-features-body">
          <div class="et-cols">
            <!-- Criteri utilizzati -->
            <div class="et-col">
              <div class="et-col-head">{{ $t('efficiencyTestDetails.summary.criteriaUsedCount') }}</div>
              <div class="et-col-line" v-for="(p, index) in getSummary.criteriaUsed" :key="'cr' + index">
                {{ $t('efficiencyTestDetails.comparisonCriteria.criteria.' + p + '.label') }}
              </div>
            </div>

            <!-- Comparabili -->
            <div class="et-col">
              <div class="et-col-head">{{ $t('efficiencyTestDetails.summary.comparablesCount') }}</div>
              <div
                class="et-kv"
                v-for="(p, index) in [
                  'primaryMarketIsinCount',
                  'secondaryMarketIsinCount',
                  'includedIsinCount',
                  'excludedIsinCount',
                ]"
                :key="'kv' + index"
              >
                <span class="et-kv-label">{{ $t(`efficiencyTests.${p}`) }}</span>
                <span class="et-kv-value">{{ getSummary[p] || 0 }}</span>
              </div>
            </div>

            <!-- Modello di scoring -->
            <div class="et-col">
              <div class="et-col-head">
                {{ $t('efficiencyTestDetails.features.colModel') }}
                <template v-if="getSummary.scoringProfileModelName">
                  <span class="et-col-head-sep">·</span> {{ getSummary.scoringProfileModelName }}
                </template>
                <!-- OKV-262: the version of the model the test was calculated with (from the snapshot). -->
                <template v-if="modelVersionLabel">
                  <span class="et-col-head-sep">·</span> {{ modelVersionLabel }}
                </template>
              </div>
              <div class="et-driver" v-for="(p, index) in getSummary.drivers" :key="'dr' + index">
                <div class="et-driver-top">
                  <span class="et-driver-name">{{ $t(`drivers.categories.${p.driver.toUpperCase()}`) }}</span>
                  <span class="et-driver-weight">{{ $n(p.weight, 'percent') }}</span>
                </div>
                <div class="et-bar">
                  <div class="et-bar-fill" :style="{ width: Math.min(Math.max((p.weight || 0) * 100, 0), 100) + '%' }"></div>
                </div>
                <!-- OKV-262: the family's indicators as used by the calculation (snapshot only). -->
                <div
                  class="et-indicator"
                  v-for="(ind, indIndex) in familyIndicators(p)"
                  :key="'ind' + index + '-' + indIndex"
                >
                  <span class="et-indicator-name">{{ indicatorLabel(ind.code) }}</span>
                  <span v-if="ind.weight != null" class="et-indicator-weight">{{ indicatorWeight(ind.weight) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- OKV-234: coverage counts as a band here (was the standalone "Copertura profilo" block
               above the panel) — moving it inside frees vertical space at the top. -->
          <div v-if="dataCoverage" class="et-coverage">
            <profile-coverage :dataCoverage="dataCoverage" :show-title="false"></profile-coverage>
          </div>

          <!-- OKV-234: reusable FKE summary — here the peer narrative. Loads on expand (:active),
               gated by the shared permission inside the component; it renders nothing without it, so
               the wrapper is also gated to avoid an empty bordered strip. -->
          <div v-if="canViewFkeSummary" class="et-narrative">
            <fke-narrative-summary
              :title="$t('efficiencyTestDetails.narrative.title')"
              :query="peerSummaryQuery"
              :revision-ids="comparablesRevisionIds"
              :cache-key="peerSummaryCacheKey"
              :active="isOpen"
            ></fke-narrative-summary>
          </div>
        </div>
      </div>
    </b-col>
  </b-row>
</template>

<script>
import SelectedFilters from '../ScoringProfiles/SelectedFilters.vue'
import EfficiencyTestOutcome from './EfficiencyTestOutcome.vue'
import ProfileCoverage from './ProfileCoverage.vue'
import FkeNarrativeSummary from '../Lib/FkeNarrativeSummary.vue'
import { calculateDriversWeightsLists } from '../../utils/drivers'
import { getFamilyIndicators, parseSnapshotDateTime } from '../../utils/modelSnapshot'
import { INIT_SEARCH_FILTER, INIT_OPT_RD } from '../../settings/scoringProfilesSettings'
import { cloneObject } from '../../imports/lib'
import { FKE_SUMMARY_PERMISSION } from '../../utils/fkeSummary'
import { mapGetters as mapGettersRoot } from 'vuex'

// OKV-234: the peer-narrative FKE query for this usage of FkeNarrativeSummary. Other usages (e.g. a
// product description on the product page) pass their own query to the same component.
const PEER_SUMMARY_QUERY =
  'Riassumimi brevemente, per ciascun prodotto: costi totali da KID (RIY), payoff (cedola, barriera, ' +
  'autocall), rischi (rating emittente, SRI, distanza dalla barriera) e rendimento annuo a scadenza nei ' +
  'casi di rimborso anticipato, barriera rispettata e barriera violata. Usa solo i dati dei documenti e ' +
  'scrivi “n.d.” se un dato manca.'

export default {
  components: {
    SelectedFilters,
    EfficiencyTestOutcome,
    ProfileCoverage,
    FkeNarrativeSummary,
  },
  props: {
    // Coverage counts shown as a band inside the panel (passed down from EfficiencyTestsDetails,
    // already filtered). OKV-234 moved this here from a standalone block above the panel.
    dataCoverage: {
      type: Object,
      default: null,
    },
    // Test id — used to build the per-test FKE summary cache key.
    testId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      isOpen: false,
      initSearchFilters: INIT_SEARCH_FILTER(),
      perimeterType: '0',
      peerSummaryQuery: PEER_SUMMARY_QUERY,
    }
  },
  methods: {
    getDriversWeightsList() {
      return calculateDriversWeightsLists(
        null,
        this.getSelectedScoringDefinition.list,
        this.getSelectedScoringDefinition.config,
        this.getSequenceWeightsDecay,
        null
      )
    },
    toggleOpen() {
      this.isOpen = !this.isOpen
    },
    familyIndicators(driver) {
      return getFamilyIndicators(driver)
    },
    // Weights inside a family are usually round (100%, 50%): no trailing ",00" (33,33% stays as it is).
    indicatorWeight(weight) {
      return this.$n(weight, { key: 'percent', minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },
    // An indicator code without a translation is shown as it is.
    indicatorLabel(code) {
      const key = `drivers.codes.${code.toUpperCase()}`
      return this.$te(key) ? this.$t(key) : code
    },
  },
  computed: {
    ...mapGettersRoot(['getTimeHorizonIntervalsList']),

    _dataFilters() {
      const tmp = { ...this.getCurrentFilters }

      tmp.perRD = INIT_OPT_RD()
      if (tmp.perSD) {
        this.perimeterType = '1'
        tmp.perSD.mad = new Date(tmp.perSD.mad)
      }
      return tmp
    },

    getCountIndicators() {
      return this.getSelectedModelId ? this.getSortedModelDrivers.length : this.getSelectedScoringDefinition.list.length
    },

    getCountFilters() {
      let count = 0
      let filters = cloneObject(this.getCurrentFilters)

      // Never set in filters tab
      delete filters.perRD

      for (const [key, value] of Object.entries(filters)) {
        if (
          Object.keys(this.initSearchFilters).includes(key) &&
          ((value !== null && value !== undefined && value !== '' && value !== false && !Array.isArray(value)) ||
            (Array.isArray(value) && value.length > 0))
        ) {
          count++
        }
      }
      return count
    },

    getCurrentFilters() {
      return this.$store.getters['efficiencyTestsFilters/getCurrentFilters']
    },

    getSelectedScoringDefinition() {
      return this.$store.getters['efficiencyTestsDefinition/getSelectedScoringDefinition']
    },

    getSelectedWeightType() {
      return this.$store.getters['efficiencyTestsDefinition/getSelectedWeightType']
    },

    getSequenceWeightsDecay() {
      return this.$store.getters['efficiencyTestsDefinition/getSequenceWeightsDecay']
    },

    getSortedModelDrivers() {
      return this.$store.getters['efficiencyTestsDefinition/getSortedModelDrivers']
    },

    getSelectedModelId() {
      return this.$store.getters['efficiencyTestsDefinition/getSelectedModelId']
    },

    getSelectedModelName() {
      return this.$store.getters['efficiencyTestsDefinition/getSelectedModelName']
    },

    getCountExcludedProducts() {
      return this.$store.getters['efficiencyTestsCoverageDetail/getExcludedProducts'].length
    },

    getSummary() {
      return this.$store.getters['efficiencyTestsDetails/getSummary']
    },

    // "versione del 19/10/2026 08:48" from the snapshot's scoringProfileLastEditedAt; '' without a valid one.
    modelVersionLabel() {
      const parsed = parseSnapshotDateTime(this.getSummary && this.getSummary.scoringProfileLastEditedAt)
      if (!parsed) return ''
      return this.$t('efficiencyTestDetails.features.modelVersion', {
        date: this.$d(parsed.date, 'short') + ' ' + parsed.time,
      })
    },

    // OKV-234: the FKE summary is gated by a single shared permission. Gate the wrapper here too so
    // we don't render an empty bordered strip when the component itself renders nothing.
    canViewFkeSummary() {
      return this.$store.getters.can(FKE_SUMMARY_PERMISSION)
    },
    // The comparables' revisionIds — the documents the peer summary is built from.
    comparablesRevisionIds() {
      return this.$store.getters['efficiencyTestsDetails/getComparablesRevisionIds']
    },
    // Per-test cache key (language is appended inside the component).
    peerSummaryCacheKey() {
      return this.testId != null ? `peer.${this.testId}` : ''
    },
  },
}
</script>

<style lang="scss" scoped>
.border-none {
  border: 0;
}

/* ── Restyled "Caratteristiche test" card ─────────────────────────────────── */
.et-features-card {
  background: #fff;
  border: 1px solid #e6e8eb;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}

.et-features-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 16px;
  // Same header background as the scoring-profiles "Caratteristiche profilo" collapsible
  // (CustomCollapse's .boxed). Header only — the body stays white.
  background: #ccd5e2;
}
.et-features-head.is-open {
  border-bottom: 1px solid #ececf0;
}
.et-features-title {
  font-weight: 700;
  font-size: 14px;
  color: #1f2430;
  margin-right: 4px;
}
.et-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  flex: 1;
}
.et-chip {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 3px 12px;
  background: #fff;
  border: 1px solid #e3e6ea;
  border-radius: 16px;
  font-size: 12.5px;
  white-space: nowrap;
}
.et-chip-label {
  color: #8a93a2;
}
.et-chip-value {
  font-weight: 700;
  color: #1f2430;
}

.et-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  padding: 0;
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 500;
  color: var(--primary, #ea633e);
  cursor: pointer;
  white-space: nowrap;
}
.et-toggle-icon {
  font-size: 12px;
}

.et-features-body {
  padding: 18px 16px 16px;
  background: #fff;
}
.et-cols {
  display: flex;
  gap: 0;
}
.et-col {
  flex: 1;
  min-width: 0;
  padding: 0 22px;
  border-left: 1px solid #ececf0;
}
.et-col:first-child {
  padding-left: 0;
  border-left: none;
}
.et-col:last-child {
  padding-right: 0;
}
.et-col-head {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #99a1ad;
  margin-bottom: 12px;
}
.et-col-head-sep {
  margin: 0 1px;
}

.et-col-line {
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
  padding: 3px 0;
}

.et-kv {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  color: #374151;
  padding: 3px 0;
}
.et-kv-value {
  font-weight: 700;
  color: #1f2430;
  font-variant-numeric: tabular-nums;
}

.et-driver {
  margin-bottom: 12px;
}
.et-driver:last-child {
  margin-bottom: 0;
}
.et-driver-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5px;
}
.et-driver-name {
  font-size: 13px;
  color: #374151;
}
.et-driver-weight {
  font-size: 13px;
  font-weight: 700;
  color: #1f2430;
  font-variant-numeric: tabular-nums;
}
.et-bar {
  height: 4px;
  border-radius: 3px;
  background: #edeef1;
  overflow: hidden;
}
.et-bar-fill {
  height: 100%;
  border-radius: 3px;
  background: var(--primary, #ea633e);
}

.et-indicator {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  line-height: 1.4;
  color: #8a93a2;
  padding-left: 10px;
  margin-top: 4px;
}
.et-bar + .et-indicator {
  margin-top: 6px;
}
.et-indicator-weight {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.et-coverage {
  margin-top: 18px;
}
// ProfileCoverage's .boxed is a global style; round it here so the band reads as a strip.
.et-coverage /deep/ .boxed {
  border-radius: 6px;
}

.et-narrative {
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid #ececf0;
}
</style>
